import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createDeepLinkingPageEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { isSupportedMobileBrowser } from '../../base/environment/environment';
import { translate } from '../../base/i18n/functions';
import Platform from '../../base/react/Platform.web';
import { openWebApp } from '../actions';
import { _TNS } from '../constants';
import { generateDeepLinkingURL } from '../functions';
const PADDINGS = {
    topBottom: 24,
    leftRight: 40
};
const useStyles = makeStyles()((_theme) => {
    return {
        container: {
            background: 'radial-gradient(1200px 600px at 50% 30%, rgba(28, 96, 255, 0.10), rgba(255, 255, 255, 0) 60%),\n                linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%)',
            width: '100vw',
            height: '100dvh',
            overflowX: 'hidden',
            overflowY: 'auto',
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center'
        },
        contentPane: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            padding: `${PADDINGS.topBottom}px ${PADDINGS.leftRight}px`,
            maxWidth: 520,
            color: '#131519',
            textAlign: 'center'
        },
        logo: {
            height: 80,
            width: 'auto',
            maxWidth: 'min(520px, 90vw)',
            objectFit: 'contain',
            marginBottom: 24,
            '@media (min-width: 768px)': {
                height: 96
            }
        },
        title: {
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            margin: '0 0 10px',
            '@media (min-width: 768px)': {
                fontSize: 28
            }
        },
        subtitle: {
            fontSize: 16,
            lineHeight: 1.55,
            color: 'rgba(19, 21, 25, 0.68)',
            margin: 0
        },
        room: {
            marginTop: 10,
            fontSize: 14,
            color: 'rgba(19, 21, 25, 0.55)',
            wordBreak: 'break-word'
        },
        fallback: {
            marginTop: 18,
            fontSize: 14
        },
        fallbackLink: {
            color: '#1c60ff',
            fontWeight: 600,
            textDecoration: 'none'
        },
        spinner: {
            marginTop: 22,
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: '2px solid rgba(19, 21, 25, 0.18)',
            borderTopColor: 'rgba(28, 96, 255, 0.9)',
            animation: 'medicaSpin 900ms linear infinite'
        },
        '@keyframes medicaSpin': {
            from: {
                transform: 'rotate(0deg)'
            },
            to: {
                transform: 'rotate(360deg)'
            }
        }
    };
});
const DeepLinkingMobilePage = ({ t }) => {
    const deeplinkingCfg = useSelector((state) => state['features/base/config']?.deeplinking || {});
    const deepLinkingUrl = useSelector(generateDeepLinkingURL);
    const room = useSelector((state) => decodeURIComponent(state['features/base/conference'].room || ''));
    const dispatch = useDispatch();
    const { classes: styles } = useStyles();
    const generateDownloadURL = useCallback(() => {
        const { downloadLink } = (deeplinkingCfg?.[Platform.OS] || {});
        return downloadLink;
    }, [deeplinkingCfg]);
    const onLaunchWeb = useCallback(() => {
        sendAnalytics(createDeepLinkingPageEvent('clicked', 'launchWebButton', { isMobileBrowser: true }));
        dispatch(openWebApp());
    }, [dispatch]);
    const onFallbackJoinInBrowser = useCallback((e) => {
        e.preventDefault();
        onLaunchWeb();
    }, [onLaunchWeb]);
    useEffect(() => {
        sendAnalytics(createDeepLinkingPageEvent('displayed', 'DeepLinkingMobile', { isMobileBrowser: true }));
    }, []);
    useEffect(() => {
        if (!isSupportedMobileBrowser()) {
            return;
        }
        // Prefer "Join in browser" by default on mobile. Render the page first
        // so users see a "Please wait" state, then navigate.
        sendAnalytics(createDeepLinkingPageEvent('clicked', 'launchWebButton', { isMobileBrowser: true, auto: true }));
        const timeout = window.setTimeout(() => {
            dispatch(openWebApp());
        }, 50);
        return () => window.clearTimeout(timeout);
    }, [dispatch]);
    return (React.createElement("div", { className: styles.container },
        React.createElement("div", { className: styles.contentPane },
            React.createElement("img", { alt: 'Medica \u2014 The heartbeat of healthcare', className: styles.logo, src: 'images/medica-logo.png' }),
            React.createElement("h1", { className: styles.title }, isSupportedMobileBrowser()
                ? 'Please wait\u2026'
                : t(`${_TNS}.unsupportedBrowser`)),
            isSupportedMobileBrowser() ? (React.createElement(React.Fragment, null,
                React.createElement("p", { className: styles.subtitle }, "Opening this consultation in your browser."),
                room ? React.createElement("div", { className: styles.room }, room) : null,
                React.createElement("div", { "aria-label": 'Loading', className: styles.spinner }),
                React.createElement("div", { className: styles.fallback },
                    "If you are not redirected,",
                    ' ',
                    React.createElement("a", { className: styles.fallbackLink, href: deepLinkingUrl, onClick: onFallbackJoinInBrowser }, "tap here to join in browser"),
                    "."))) : (React.createElement(React.Fragment, null,
                React.createElement("p", { className: styles.subtitle }, "Your browser is not supported for joining this meeting."),
                React.createElement("div", { className: styles.fallback },
                    React.createElement("a", { className: styles.fallbackLink, href: generateDownloadURL(), target: '_top' }, t(`${_TNS}.downloadMobileApp`))))))));
};
export default translate(DeepLinkingMobilePage);
